import type { ReactNode } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { net, type Summary } from "../../lib/stats";
import type { LiteTrade } from "../../lib/types";
import { SideTag } from "../../ui/TradeList";
import { Card, Empty, Pnl } from "../../ui/ui";

export interface MiniRow { key: string; label: ReactNode; s: Summary }

/** Petit tableau trades / gagnants / résultat (symboles, stratégies). */
export function MiniTable({ title, rows, empty, className = "" }: { title: string; rows: MiniRow[]; empty: string; className?: string }) {
  const t = useTranslation().lite.blocks;
  const { fmt } = useLiteData();
  return (
    <Card title={title} pad={false} className={className}>
      {rows.length ? (
        <div className="max-h-[340px] overflow-y-auto">
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="text-[11px] text-muted-foreground/70 border-b border-border">
                <th className="text-left font-normal px-5 py-2" />
                <th className="text-right font-normal px-2 py-2">{t.trades}</th>
                <th className="text-right font-normal px-2 py-2">{t.winnersShort}</th>
                <th className="text-right font-normal px-5 py-2">{t.result}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map(r => (
                <tr key={r.key}>
                  <td className="px-5 py-2.5 max-w-[140px] truncate">{r.label}</td>
                  <td className="px-2 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-muted-foreground">{r.s.count}</td>
                  <td className="px-2 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-muted-foreground">{fmt.pct(r.s.winRate)}</td>
                  <td className="px-5 py-2.5 text-right whitespace-nowrap"><Pnl value={r.s.netPnl} className="font-medium" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty title={empty} />}
    </Card>
  );
}

/** 10 derniers trades ; clic sur une ligne = ouverture du tiroir. */
export function RecentTable({ trades, onSelect }: { trades: LiteTrade[]; onSelect: (id: string) => void }) {
  const t = useTranslation().lite;
  const r = t.dashboard.recent;
  const { fmt, accountsById, strategies } = useLiteData();
  const strategyById = new Map(strategies.map(s => [s.id, s]));
  const heads = [r.date, r.symbol, r.side, r.account, r.strategy, r.qty, r.entry, r.exit, r.duration, r.result];

  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-sm min-w-[900px]">
        <thead>
          <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
            {heads.map((h, i) => (
              <th key={i} className={`font-normal py-2 ${i === 0 ? "pl-5 text-left" : i >= 5 ? "px-3 text-right" : "px-3 text-left"} ${i === 9 ? "pr-5" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {trades.map(tr => {
            const st = tr.strategyIds.map(id => strategyById.get(id)).find(Boolean);
            const account = tr.accountId ? accountsById.get(tr.accountId) : undefined;
            return (
              <tr key={tr.id} onClick={() => onSelect(tr.id)} className="cursor-pointer hover:bg-muted/60 transition-colors">
                <td className="pl-5 py-2.5 font-mono tabular-nums text-xs text-muted-foreground whitespace-nowrap">{fmt.dateShort(tr.openedAt)} {fmt.time(tr.openedAt)}</td>
                <td className="px-3 py-2.5 font-semibold">{tr.symbol}</td>
                <td className="px-3 py-2.5"><SideTag side={tr.side} /></td>
                <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{account?.name ?? t.trade.noAccount}</td>
                <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                  {st ? (
                    <><span className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle" style={{ background: st.color }} />{st.name}</>
                  ) : "-"}
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{tr.qty}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmt.price(tr.entry)}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{tr.exit !== null ? fmt.price(tr.exit) : "-"}</td>
                <td className="px-3 py-2.5 text-right text-muted-foreground whitespace-nowrap">
                  {tr.closed ? fmt.duration(new Date(tr.closedAt).getTime() - new Date(tr.openedAt).getTime()) : "-"}
                </td>
                <td className="pr-5 pl-3 py-2.5 text-right whitespace-nowrap">
                  {tr.closed ? <Pnl value={net(tr)} decimals className="font-medium" /> : <span className="text-xs text-muted-foreground">{t.trade.open}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
